import { logWarn, logError } from '../../shared/utils/logger';
import { setTokens as persistTokens } from '../../shared/utils/secureTokenStorage';
import { BASE_URL, normalizeTokens } from './client';

const FALLBACK_API_BASE = "https://api.cloudmortgage.io";
const TENANT_ID = "cloudrealtor.io";

const parseJsonSafely = async (response) => {
    try {
        return await response.json();
    } catch (error) {
        return null;
    }
};

const fetchAuthJson = async (path, options) => {
    const primaryUrl = `${BASE_URL}${path}`;
    const origin = typeof window !== "undefined" ? window.location?.origin : "";

    // Add X-Tenant-Id header to all auth requests
    const optionsWithTenant = {
        ...options,
        headers: {
            ...options.headers,
            "X-Tenant-Id": TENANT_ID,
        },
    };

    // Check if we should use fallback API:
    // - On cloudrealtor.io or cloudmortgage.io production domains
    // - On Amplify preview URLs (*.amplifyapp.com)
    // - When BASE_URL is empty (no explicit API configured)
    const isAmplifyPreview = origin.includes(".amplifyapp.com");
    const isProductionDomain = origin === "https://cloudrealtor.io" || origin === "https://cloudmortgage.io";
    const shouldFallback = (isAmplifyPreview || isProductionDomain) && BASE_URL === "";

    // If we know we need fallback, go directly to it
    if (shouldFallback) {
        const fallbackResponse = await fetch(`${FALLBACK_API_BASE}${path}`, optionsWithTenant);
        return fallbackResponse;
    }

    const response = await fetch(primaryUrl, optionsWithTenant);
    const contentType = response.headers.get("content-type") || "";
    if (response.ok && contentType.includes("application/json")) {
        return response;
    }
    return response;
};

/**
 * RQ: { email: string, password: string }
 * RS: { status: 200, data: { user: { email: string } } }
 */
export async function login(payload) {
    const body = {
        password: payload.password,
        email: payload.email,
    };
    // Only include role if provided (for backwards compatibility)
    if (payload.role) {
        body.role = payload.role;
    }

    try {
        const response = await fetchAuthJson("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            throw new Error("Invalid email or password.");
        }

        const data = await response.json();

        // Handle different token response formats - check nested structures
        const tokenData =
            data?.token ||           // Direct: {token: "..."}
            data?.tokens ||          // Direct: {tokens: {...}}
            data?.data?.token ||     // Nested: {data: {token: "..."}}
            data?.data?.tokens ||    // Nested: {data: {tokens: {...}}}
            data?.accessToken ||     // Direct: {accessToken: "..."}
            data?.data;              // Fallback: use data.data object

        if (tokenData) {
            // Get role from server response - server is authoritative
            const userRole = data?.data?.user?.role || data?.user?.role;

            if (!userRole) {
                throw new Error("Unable to sign in. Please try again.");
            }

            const tokensWithRole = {
                ...normalizeTokens(tokenData),
                role: userRole,
            };
            // SECURITY: Use sessionStorage by default (clears on browser close)
            // Set rememberMe: true only if user explicitly requests it
            persistTokens(tokensWithRole, false);

            // Ensure role is in response for AuthScreen
            if (!data.user) data.user = {};
            data.user.role = userRole;
        } else {
            logWarn("Login response did not contain token data");
        }

        return { status: response.status, data };
    } catch (error) {
        logError("Login error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, password: string, referral_id?: string }
 * RS: { status: 200, data: { user: { email: string } } }
 */
export async function signup(payload) {
    // Get Rewardful referral ID if available
    const referralId = payload.referral_id || (typeof window !== 'undefined' && window.Rewardful?.referral) || null;

    const body = {
        email: payload.email,
        password: payload.password,
        username: payload.username || payload.email,
        membership: payload.membership || "Free",
        additionalProp1: {
            firstName: payload.firstName,
            lastName: payload.lastName,
            company: payload.company,
        },
        role: payload.role || "Lender"
    };

    // Include referral_id if present (for affiliate tracking)
    if (referralId) {
        body.referral_id = referralId;
    }
    try {
        const response = await fetchAuthJson("/api/auth/signup", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            // Check for specific error codes
            if (response.status === 409) {
                throw new Error("USER_EXISTS");
            }
            throw new Error("Unable to create account. Please try again.");
        }

        const data = await response.json();

        // Handle different token response formats - check nested structures
        const tokenData =
            data?.token ||           // Direct: {token: "..."}
            data?.tokens ||          // Direct: {tokens: {...}}
            data?.data?.token ||     // Nested: {data: {token: "..."}}
            data?.data?.tokens ||    // Nested: {data: {tokens: {...}}}
            data?.accessToken ||     // Direct: {accessToken: "..."}
            data?.data;              // Fallback: use data.data object

        if (tokenData) {
            // Include role in stored tokens for RBAC
            const userRole = data?.data?.user?.role || data?.user?.role || body.role;
            const tokensWithRole = {
                ...normalizeTokens(tokenData),
                role: userRole,
            };
            // SECURITY: Use sessionStorage by default (clears on browser close)
            // Set rememberMe: true only if user explicitly requests it
            persistTokens(tokensWithRole, false);

            // Ensure role is in response for AuthScreen
            if (!data.user) data.user = {};
            data.user.role = userRole;
        } else {
            logWarn("Signup response did not contain token data");
        }

        return { status: response.status, data };
    } catch (error) {
        logError("Signup error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string }
 * RS: { status: 200, data: { message: string } }
 */
export async function requestSignupCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/signup/request-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            throw new Error(errorData?.message || "Unable to send verification code.");
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Signup code request error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string }
 * RS: { status: 200, data: { verified: boolean } }
 */
export async function verifySignupCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/signup/verify-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email, code: payload.code })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Invalid or expired verification code.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Signup code verification error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string }
 * RS: { status: 200, data: { message: string, expiresIn: number } }
 */
export async function requestPasswordResetCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/request-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            throw new Error(errorData?.message || "Unable to send reset code.");
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset code request error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string }
 * RS: { status: 200, data: { verified: boolean } }
 */
export async function verifyPasswordResetCode(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/verify-code", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email, code: payload.code })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Invalid or expired reset code.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset code verification error:", error);
        throw error;
    }
}

/**
 * RQ: { email: string, code: string, newPassword: string }
 * RS: { status: 200, data: { message: string } }
 */
export async function resetPassword(payload) {
    try {
        const response = await fetchAuthJson("/api/auth/password/reset", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email: payload.email,
                code: payload.code,
                newPassword: payload.newPassword
            })
        });

        if (!response.ok) {
            const errorData = await parseJsonSafely(response);
            const message = errorData?.message || "Unable to reset password.";
            throw new Error(message);
        }

        const data = await parseJsonSafely(response);
        return { status: response.status, data };
    } catch (error) {
        logError("Password reset error:", error);
        throw error;
    }
}
